import React, { useState, useEffect } from 'react';
import { X, Download, Share, MoreVertical, Monitor, Smartphone, Apple, Trash2, Check } from 'lucide-react';
import { sePuedeInstalar, alCambiarInstalable, instalarApp, estaInstalada } from '../../utils/pwa';

/**
 * Cómo instalar (o desinstalar) CholloMan en el dispositivo.
 *
 * Hace falta explicarlo porque cada plataforma lo resuelve distinto y ninguna
 * lo hace evidente:
 *
 *  - **Android/Chrome** permite abrir el diálogo del sistema desde un botón,
 *    pero sólo si guardamos antes el evento `beforeinstallprompt`.
 *  - **iOS/Safari** no tiene ese diálogo: la instalación es manual desde
 *    Compartir → Añadir a inicio, y nadie lo descubre solo.
 *  - **Escritorio** tiene un icono en la barra de direcciones que pasa
 *    desapercibido.
 *
 * Las tres van en pestañas y no una debajo de la otra: mostrar todo junto
 * obliga a leer instrucciones de dos sistemas que no son el propio para
 * encontrar el suyo.
 */

const ANDROID = 'android';
const IOS = 'ios';
const ESCRITORIO = 'escritorio';

/** Qué pestaña abrir primero, según desde dónde se esté mirando. */
function plataformaProbable() {
  if (typeof navigator === 'undefined') return ANDROID;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return IOS;
  if (/Android/i.test(ua)) return ANDROID;
  return ESCRITORIO;
}

const PLATAFORMAS = [
  { id: ANDROID,    label: 'Android',  icono: Smartphone },
  { id: IOS,        label: 'iPhone',   icono: Apple },
  { id: ESCRITORIO, label: 'PC',       icono: Monitor },
];

/** Un paso numerado de las instrucciones. */
const Paso = ({ n, children }) => (
  <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
    <span style={{
      flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
      background: '#fff1eb', color: '#c2410c', fontSize: '0.72rem', fontWeight: 800,
      display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
    }}>{n}</span>
    <span style={{ fontSize: '0.86rem', color: '#374151', lineHeight: 1.55 }}>{children}</span>
  </li>
);

const Resaltado = ({ children, icono: Icono }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: '#f3f4f6', borderRadius: 5, padding: '1px 6px',
    fontWeight: 600, color: '#111', whiteSpace: 'nowrap',
  }}>
    {Icono && <Icono size={12} />}
    {children}
  </span>
);

/* ── Instrucciones para instalar ─────────────────────────────────────────── */

const INSTALAR = {
  [ANDROID]: [
    <>Abrí CholloMan en <strong>Chrome</strong>.</>,
    <>Tocá el menú <Resaltado icono={MoreVertical}>⋮</Resaltado> arriba a la derecha.</>,
    <>Elegí <Resaltado>Instalar aplicación</Resaltado> o <Resaltado>Añadir a pantalla de inicio</Resaltado>.</>,
    <>Confirmá. El ícono queda con el resto de tus apps.</>,
  ],
  [IOS]: [
    <>Abrí CholloMan en <strong>Safari</strong>. Desde Chrome en iPhone no se puede.</>,
    <>Tocá <Resaltado icono={Share}>Compartir</Resaltado>, el cuadrado con la flecha hacia arriba.</>,
    <>Bajá y elegí <Resaltado>Añadir a inicio</Resaltado>.</>,
    <>Tocá <Resaltado>Añadir</Resaltado> arriba a la derecha.</>,
  ],
  [ESCRITORIO]: [
    <>Abrí CholloMan en <strong>Chrome</strong> o <strong>Edge</strong>.</>,
    <>Buscá el ícono <Resaltado icono={Download}>Instalar</Resaltado> a la derecha de la barra de direcciones.</>,
    <>Si no lo ves, entrá al menú <Resaltado icono={MoreVertical}>⋮</Resaltado> y elegí <Resaltado>Instalar CholloMan</Resaltado>.</>,
    <>Se abre en su propia ventana, sin pestañas ni barra del navegador.</>,
  ],
};

/* ── Instrucciones para desinstalar ──────────────────────────────────────── */

const DESINSTALAR = {
  [ANDROID]: [
    <>Mantené presionado el ícono de CholloMan en la pantalla de inicio.</>,
    <>Elegí <Resaltado>Desinstalar</Resaltado> o arrastralo a la papelera.</>,
    <>También podés hacerlo desde <Resaltado>Ajustes → Aplicaciones → CholloMan</Resaltado>.</>,
  ],
  [IOS]: [
    <>Mantené presionado el ícono de CholloMan en la pantalla de inicio.</>,
    <>Elegí <Resaltado>Eliminar app</Resaltado> y confirmá.</>,
    <>Se borra como cualquier otra aplicación; tu cuenta no se toca.</>,
  ],
  [ESCRITORIO]: [
    <>Abrí CholloMan desde su ventana instalada.</>,
    <>Entrá al menú <Resaltado icono={MoreVertical}>⋮</Resaltado> de esa ventana.</>,
    <>Elegí <Resaltado>Desinstalar CholloMan</Resaltado> y confirmá.</>,
  ],
};

export const InstalarApp = ({ onCerrar }) => {
  const [plataforma, setPlataforma] = useState(plataformaProbable);
  const [instalable, setInstalable] = useState(sePuedeInstalar());
  const instalada = estaInstalada();
  const [instalando, setInstalando] = useState(false);

  useEffect(() => alCambiarInstalable(setInstalable), []);

  useEffect(() => {
    const alPulsar = (e) => { if (e.key === 'Escape') onCerrar?.(); };
    document.addEventListener('keydown', alPulsar);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alPulsar);
      document.body.style.overflow = overflowPrevio;
    };
  }, [onCerrar]);

  const pasos = (instalada ? DESINSTALAR : INSTALAR)[plataforma] || [];

  const instalarAhora = async () => {
    setInstalando(true);
    const acepto = await instalarApp();
    setInstalando(false);
    if (acepto) onCerrar?.();
  };

  return (
    <div
      id="instalar-app-overlay"
      className="modal-overlay dialogo dialogo--instalar"
      onClick={onCerrar}
    >
      <div
        id="instalar-app"
        className="modal-caja dialogo__caja dialogo__caja--ancha"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="instalar-app-titulo"
      >
        <button
          id="instalar-app-cerrar"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="tap-exempt"
          style={{
            position: 'absolute', top: 10, right: 10, background: 'none',
            border: 'none', cursor: 'pointer', color: '#9ca3af',
            padding: 8, display: 'flex', borderRadius: 8,
          }}
        >
          <X size={20} />
        </button>

        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: instalada ? '#f3f4f6' : '#fff1eb',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
        }}>
          {instalada ? <Trash2 size={20} color="#6b7280" /> : <Download size={20} color="#FF4500" />}
        </div>

        <h2
          id="instalar-app-titulo"
          style={{
            fontWeight: 800, fontSize: '1.12rem',
            color: '#111', letterSpacing: '-0.02em', margin: '0 0 6px', lineHeight: 1.25,
          }}
        >
          {instalada ? 'Desinstalar CholloMan' : 'Instalá CholloMan en tu dispositivo'}
        </h2>

        <p style={{ fontSize: '0.87rem', color: '#4b5563', lineHeight: 1.55, margin: '0 0 14px' }}>
          {instalada
            ? 'Ya la tenés instalada. Si querés sacarla, elegí tu dispositivo:'
            : 'Se abre como una app, sin barra del navegador, y funciona aunque te quedes sin señal.'}
        </p>

        {instalada && (
          <p id="instalar-app-estado" style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: '0.78rem', color: '#16a34a', margin: '0 0 14px',
          }}>
            <Check size={13} /> Estás usando la app instalada
          </p>
        )}

        {/* Instalación en un toque, cuando el navegador la permite. Sólo Chrome
            y Edge; en iPhone no existe y hay que explicar el camino manual. */}
        {!instalada && instalable && (
          <button
            id="instalar-app-directo"
            onClick={instalarAhora}
            disabled={instalando}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', minHeight: 48, borderRadius: 10, border: 'none',
              background: instalando ? '#fdba74' : '#FF4500', color: '#fff',
              fontSize: '0.9rem', fontWeight: 700, cursor: instalando ? 'wait' : 'pointer',
              marginBottom: 16,
            }}
          >
            <Download size={16} />
            {instalando ? 'Instalando…' : 'Instalar ahora'}
          </button>
        )}

        {/* Selección de plataforma: sólo se muestran los pasos de la elegida. */}
        <div
          id="instalar-app-plataformas"
          role="tablist"
          style={{ display: 'flex', gap: 6, background: '#f3f4f6', borderRadius: 10, padding: 4, marginBottom: 14 }}
        >
          {PLATAFORMAS.map(({ id, label, icono: Icono }) => {
            const activa = id === plataforma;
            return (
              <button
                key={id}
                id={`instalar-app-tab-${id}`}
                role="tab"
                aria-selected={activa}
                onClick={() => setPlataforma(id)}
                style={{
                  flex: 1, minHeight: 40, borderRadius: 8, border: 'none',
                  background: activa ? '#fff' : 'transparent',
                  color: activa ? '#111' : '#6b7280',
                  fontWeight: activa ? 700 : 500, fontSize: '0.82rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  boxShadow: activa ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                <Icono size={14} />
                {label}
              </button>
            );
          })}
        </div>

        <ol
          id="instalar-app-pasos"
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          {pasos.map((paso, i) => <Paso key={i} n={i + 1}>{paso}</Paso>)}
        </ol>

        {!instalada && plataforma === IOS && (
          <p style={{ fontSize: '0.76rem', color: '#9ca3af', margin: '14px 0 0', lineHeight: 1.5 }}>
            En iPhone la instalación siempre es manual: Apple no permite que una
            página abra el diálogo del sistema.
          </p>
        )}
      </div>
    </div>
  );
};
